import {
    MutableRefObject,
    useContext, useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { Edge } from '../../backend/types/edge.ts'
import { useDiagramConfig } from '../../hooks/useDiagramConfig.ts'
import { rgbToHex } from './diagram/color.ts'
import { DiagramLabel } from './DiagramLabel.tsx'
import { DiagramLink } from './DiagramLink.tsx'
import { DiagramLinksLabels } from './DiagramLinksLabels.tsx'
import { DiagramNode } from './DiagramNode.tsx'
import { InteractionWrap, InteractionWrapHandle } from './InteractionWrap.tsx'
import { CustomizeMoneyFlowContext, MoneyFlowContext } from "../../context/MoneyFlowContext.tsx"
import { useCurrentEntity } from "../../hooks/useCurrentEntity.ts"
import { UserLabels } from "../../backend/types/userLabels.ts"
import { useGetUserLabels } from "../../api/labels/labels.ts"
import { NodeTooltip } from "./NodeTooltip.tsx"
import { DiagramToolbar } from './toolbar/DiagramToolbar.tsx'
import { useNodeTooltip } from "./useNodeTooltip.tsx"
import { Point } from "./diagram/point.ts"
import { Entity } from "../../backend/types/entity.ts"
import { Node } from "./diagram/diagram.ts"
import { DiagramLoadingCard } from './DiagramLoadingCard.tsx'
import { getOriginLabelId } from "../../utils/labels.ts"
import { DiagramPins } from "./DiagramPins.tsx"
import { TransactionTableTabs } from '../TransactionTable/transactionTable.tsx'
import { useParams } from 'react-router-dom'

export const MoneyFlowDiagram = () => {
    const selfRef = useRef<HTMLDivElement>() as MutableRefObject<HTMLDivElement>
    const { documentId } = useParams<{ documentId?: string }>()

    const { data: labels } = useGetUserLabels(!!documentId)
    const interactionWrapRef = useRef<InteractionWrapHandle | null>(null)
    const { setCurrentEntity, currentEntity } = useCurrentEntity()
    const [tooltipProps, setTooltipProps] = useState<{ entity?: Entity, node: Node, position: Point & { right: boolean } }>()
    const {
        show,
        setShow: setShowTooltip,
        tooltip,
        tooltipPosition,
        isPending
    } = useNodeTooltip(
        tooltipProps?.entity,
        tooltipProps?.node,
        tooltipProps?.position
    )
    const [dimensions, setDimensions] = useState<[number, number]>([800, 600])

    const {
        getEntityConfigOrDefault
    } = useDiagramConfig()

    const {
        linkProps,
        nodesProps,
        sankeyData,
        graph,
        transactionTable: { onChangeTab }
    } = useContext(MoneyFlowContext)
    const {
        highlightNode,
        removeEffects,
    } = useContext(CustomizeMoneyFlowContext)

    useEffect(() => {
        if (!selfRef.current) {
            return
        }

        const resizeObserver = new ResizeObserver(() => {
            setDimensions(() => [selfRef.current?.clientWidth || 800, selfRef.current?.clientHeight || 600])
        })
        resizeObserver.observe(selfRef.current)

        return () => {
            resizeObserver.disconnect()
        }
    }, [])

    useLayoutEffect(() => {
        const yPositions = graph.nodes.map((n) => n.position!.y)
        const xPositions = graph.nodes.map((n) => n.position!.x)

        const maxY = Math.max(...yPositions)
        const minY = Math.min(...yPositions)
        const maxX = Math.max(...xPositions)
        const minX = Math.min(...xPositions)

        const maxFullLineWidth =
            graph.links.reduce((maxHalfWidth, link) =>
                (maxHalfWidth < link.valueHalfWidth! ? link.valueHalfWidth! : maxHalfWidth), 0) * 2

        interactionWrapRef.current?.setCustomViewBox(
            minX - maxFullLineWidth,
            minY - maxFullLineWidth,
            maxX - minX + maxFullLineWidth * 2,
            maxY - minY + maxFullLineWidth * 2
        )
    }, [interactionWrapRef, graph.nodes, graph.links])

    const allNodes = useMemo(() => graph.nodes.map((node) => {
        const entity = (sankeyData?.Entities || []).find((e) => e.ID === node.semanticData.ID)

        return <DiagramNode
            key={node.id}
            path={node.path || ''}
            color={rgbToHex(node.color)}
            onClick={() => {
                if (!interactionWrapRef.current?.isPanning()) {
                    setCurrentEntity(node.semanticData.ID)
                    onChangeTab(TransactionTableTabs.ALL)
                }
            }}
            selected={(currentEntity as Entity)?.ID === node.semanticData.ID}
            opacity={nodesProps[node.id]?.opacity || 1}
            strokeOpacity={nodesProps[node.id]?.strokeOpacity || 1}
            onMouseOver={(e) => {
                const offsetX = e.clientX -
                    selfRef.current.getBoundingClientRect().x
                const offsetY = e.clientY -
                    selfRef.current.getBoundingClientRect().y
                const isOnTheRightSide = selfRef.current.clientWidth - (e.clientX -
                    selfRef.current.getBoundingClientRect().x) < selfRef.current.clientWidth / 2

                highlightNode(node.id)
                setTooltipProps({
                    entity,
                    node,
                    position: {
                        x: offsetX,
                        y: offsetY,
                        right: isOnTheRightSide,
                    }
                })
                setShowTooltip(true)
            }}
            onMouseLeave={() => {
                removeEffects()
                setShowTooltip(false)
            }}
        />
    }), [graph.nodes, sankeyData?.Entities, currentEntity, nodesProps,
        setCurrentEntity, onChangeTab, highlightNode, setShowTooltip, removeEffects])

    const allLinks = useMemo(() =>
        graph.links.toSorted((l1, l2) => l2.value - l1.value).map((link) => <DiagramLink
            key={link.id}
            id={link.id}
            amount={link.value}
            path={link.path!}
            selected={(currentEntity as Edge)?._id === link.semanticData._id}
            reversePath={link.reversePath}
            halfWidth={link.renderHalfWidth!}
            onClick={() => {
                if (!interactionWrapRef.current?.isPanning()) {
                    setCurrentEntity(link.id)
                    onChangeTab(TransactionTableTabs.ALL)
                }
            }}
            // TODO: BIT-7892
            color={rgbToHex(link.color || { r: 0, g: 0, b: 0 })}
            opacity={linkProps[link.id]?.opacity || link.opacity || 1}
        />), [currentEntity, graph.links, linkProps, onChangeTab, setCurrentEntity])

    const allLinksLabels = <DiagramLinksLabels
        labels={(labels?.tags || []) as UserLabels['tags']}
        interactionWrap={interactionWrapRef.current}
    />

    const allLabels = [...graph.nodes.map((node) => {
        const entity = sankeyData!.Entities.find((entity) => entity.ID === node.semanticData.ID)!
        const entityAddressLabels = entity.AddressesLabeled
            .map((val) => val.Labels?.map((l) => l.Label).join(';'))
            .join(';').slice(0, 24)
        const fallbackLabel = entity.Name || entity.ID
        const defaultLabel = entityAddressLabels || fallbackLabel.slice(0, 4) + '...' + fallbackLabel.slice(-4)
        const labelId = node.semanticData.ID === 'origin' ?
            getOriginLabelId(entity.AddressesLabeled) :
            node.semanticData.ID

        return <DiagramLabel
            key={node.id + '_label'}
            left={node.position!.x - node.extent!.x}
            right={node.position!.x + node.extent!.x}
            bottom={node.position!.y + node.extent!.y}
            addressesAmount={entity?.AddressesLabeled.length || 0}
            width={selfRef.current?.clientWidth || window.innerWidth}
            defaultLabel={defaultLabel}
            labels={labels?.tags[labelId] || []}
            color={getEntityConfigOrDefault(entity.Type).fontColor}
            fontSize={getEntityConfigOrDefault(entity.Type).fontSize}
            fontWeight={getEntityConfigOrDefault(entity.Type).fontWeight}
            onClick={() => {
                if (!interactionWrapRef.current?.isPanning()) {
                    setCurrentEntity(node.semanticData.ID)
                    onChangeTab(TransactionTableTabs.ALL)
                }
            }}
        />
    })]

    //TODO: change width and height when BIT-7019 is done
    return (
        <div
            className="sankey-container"
            ref={selfRef}
        >
            <DiagramLoadingCard>

                {!!documentId && <DiagramToolbar />}
                <InteractionWrap
                    ref={interactionWrapRef}
                    width={dimensions[0] || selfRef.current?.clientWidth}
                    height={dimensions[1] || selfRef.current?.clientHeight}
                >
                    {allLinks}
                    {allLinksLabels}
                    {allNodes}
                    {allLabels}
                    <DiagramPins graph={graph} width={selfRef.current?.clientWidth || window.innerWidth} />
                </InteractionWrap>

                {show && <NodeTooltip
                    isPending={isPending}
                    style={{
                        top: tooltipPosition?.y,
                        left: tooltipPosition?.x
                    }}
                    {...tooltip}
                />}
            </DiagramLoadingCard>
        </div>
    )
}
